import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  MessageCircle, TrendingUp, DollarSign, BarChart3, Send, Plus, Loader2, 
  AlertTriangle, Shield, Target, Zap, Building2, Settings as SettingsIcon,
  AlertCircle, Info, Menu, X, Crown, Star, Video, Lock, Bot, User, Lightbulb,
  PlusCircle, Trash2, MessageSquare, Paperclip, FileUp, Download, FileText, CreditCard, RefreshCw
} from "lucide-react";
import ReactMarkdown from 'react-markdown';
import { useAuth } from './AuthContext';
import AuthPage from './AuthPage';
import Settings from './Settings';
import PricingPage from './PricingPage';
import PaymentResult from './PaymentResult';
import ConsultancyPage from './ConsultancyPage';
import AdminPayments from './AdminPayments';
import MyAccountPage from './MyAccountPage';
import RefundRequestPage from './RefundRequestPage';
import PrivacyPage from './PrivacyPage';
import RefundPage from './RefundPage';
import TermsPage from './TermsPage';
import ContactPage from './ContactPage';
import Footer from './components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { user, loading: authLoading, getAuthHeader } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ 
    total_conversations: 0, 
    total_messages: 0, 
    recent_topics: [],
    risk_score: 0,
    has_business_profile: false
  });
  
  const [businessProfile, setBusinessProfile] = useState(null);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [profileForm, setProfileForm] = useState({
    business_type: "outro",
    custom_business_type: "",
    business_name: "",
    business_description: "",
    foundation_year: "",
    monthly_revenue: "",
    monthly_expenses: "",
    has_employees: false,
    employee_count: 0,
    location: "",
    target_audience: "",
    main_products_services: "",
    competitors: "",
    mixes_personal_business: false,
    current_debt: "",
    prolabore: ""
  });

  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [analyzingRisk, setAnalyzingRisk] = useState(false);
  const [pjpfAnalysis, setPjpfAnalysis] = useState(null);
  const [analyzingPjpf, setAnalyzingPjpf] = useState(false);
  const [crisisMode, setCrisisMode] = useState(false);
  const [crisisActionPlan, setCrisisActionPlan] = useState(null);
  const [loadingCrisis, setLoadingCrisis] = useState(false);
  const [expansionAnalysis, setExpansionAnalysis] = useState(null);
  const [analyzingExpansion, setAnalyzingExpansion] = useState(false);
  const [showExpansionForm, setShowExpansionForm] = useState(false);
  const [expansionForm, setExpansionForm] = useState({
    expansion_type: "nova_unidade",
    expansion_details: "",
    investment_available: "",
    timeline: "",
    main_goal: ""
  });
  const [cashflowData, setCashflowData] = useState({ 
    revenue: "", 
    expenses: "", 
    period: "monthly", 
    description: "" 
  });
  const [cashflowResult, setCashflowResult] = useState(null);
  const [analyzingCashflow, setAnalyzingCashflow] = useState(false);
  const [currentView, setCurrentView] = useState('chat'); // 'chat', 'settings', 'pricing', 'consultancy', 'admin', 'account', 'refund'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analyzingFile, setAnalyzingFile] = useState(null);
  const [fileAnalysisResult, setFileAnalysisResult] = useState(null);
  const fileInputRef = useRef(null);
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (user) {
      loadConversations();
      loadStats();
      loadBusinessProfile();
      loadLatestRiskAnalysis();
      loadSubscriptionStatus();
      loadFiles();
    }
  }, [user]);

  useEffect(() => {
    if (riskAnalysis) {
      setCrisisMode(riskAnalysis.crisis_mode);
    }
  }, [riskAnalysis]);

  const loadSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: getAuthHeader()
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error("Error loading subscription status:", error);
    }
  };

  const loadFiles = async () => {
    try {
      const response = await axios.get(`${API}/files`, { headers: getAuthHeader() });
      setUploadedFiles(response.data);
    } catch (error) {
      console.error("Error loading files:", error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Arquivo muito grande. Máximo: 10MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Arquivo "${response.data.filename}" enviado com sucesso!`);
      await loadFiles();
    } catch (error) {
      const errMsg = error.response?.data?.detail || "Erro ao enviar arquivo";
      toast.error(errMsg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const analyzeFile = async (fileId, question) => {
    setAnalyzingFile(fileId);
    setFileAnalysisResult(null);
    try {
      const response = await axios.get(`${API}/analyze-file`, {
        params: { file_id: fileId, question: question || undefined },
        headers: getAuthHeader()
      });
      setFileAnalysisResult(response.data);
      await loadSubscriptionStatus();
      toast.success("Análise concluída! (-3 créditos)");
    } catch (error) {
      const errMsg = error.response?.data?.detail || "Erro ao analisar arquivo";
      toast.error(errMsg);
    } finally {
      setAnalyzingFile(null);
    }
  };

  const deleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/files/${fileId}`, { headers: getAuthHeader() });
      toast.success("Arquivo removido");
      await loadFiles();
    } catch (error) {
      toast.error("Erro ao remover arquivo");
    }
  };

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`, {
        headers: getAuthHeader()
      });
      setConversations(response.data);
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard-stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const loadBusinessProfile = async () => {
    try {
      const response = await axios.get(`${API}/business-profile`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setBusinessProfile(response.data);
      }
    } catch (error) {
      console.error("Error loading business profile:", error);
    }
  };

  const loadLatestRiskAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/risk-analysis/latest`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setRiskAnalysis(response.data);
      }
    } catch (error) {
      console.error("Error loading risk analysis:", error);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}`, {
        headers: getAuthHeader()
      });
      setCurrentConversation(response.data);
      setMessages(response.data.messages || []);
      setCurrentView('chat');
    } catch (error) {
      console.error("Error loading conversation:", error);
      toast.error("Erro ao carregar conversa");
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage;
    setInputMessage("");
    setLoading(true);

    const tempUserMsg = {
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        conversation_id: currentConversation?.id,
        business_context: businessProfile?.business_type
      }, {
        headers: getAuthHeader()
      });

      const assistantMsg = {
        role: "assistant",
        content: response.data.response,
        timestamp: response.data.timestamp,
        educational_content: response.data.educational_content
      };
      
      setMessages(prev => [...prev, assistantMsg]);
      
      if (!currentConversation) {
        setCurrentConversation({ id: response.data.conversation_id });
      }
      
      await loadConversations();
      await loadStats();
      
      // Update subscription status to reflect new credit balance
      await loadSubscriptionStatus();
      
      if (response.data.educational_content) {
        toast.info("💡 Conteúdo educacional incluído na resposta");
      }
      
      // Show credits used notification
      if (response.data.credits_used && !response.data.unlimited_credits) {
        toast.success(`-${response.data.credits_used} crédito${response.data.credits_used > 1 ? 's' : ''} • Saldo: ${response.data.credits_remaining}`, {
          duration: 2000
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMsg = error.response?.data?.detail || "Erro ao enviar mensagem";
      toast.error(errorMsg);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const startNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
    setInputMessage("");
    setCurrentView('chat');
  };

  const createBusinessProfile = async () => {
    try {
      const response = await axios.post(`${API}/business-profile`, {
        ...profileForm,
        monthly_revenue: parseFloat(profileForm.monthly_revenue) || 0,
        monthly_expenses: parseFloat(profileForm.monthly_expenses) || 0,
        current_debt: parseFloat(profileForm.current_debt) || 0,
        prolabore: parseFloat(profileForm.prolabore) || 0,
        employee_count: parseInt(profileForm.employee_count) || 0,
        foundation_year: profileForm.foundation_year ? parseInt(profileForm.foundation_year) : null
      }, {
        headers: getAuthHeader()
      });
      
      setBusinessProfile(response.data);
      setShowProfileForm(false);
      toast.success("Perfil do negócio criado com sucesso!");
      setTimeout(() => analyzeRisk(), 1000);
    } catch (error) {
      console.error("Error creating profile:", error);
      toast.error("Erro ao criar perfil");
    }
  };

  const analyzeRisk = async () => {
    if (!businessProfile) {
      toast.error("Crie um perfil do negócio primeiro");
      return;
    }

    setAnalyzingRisk(true);
    try {
      const response = await axios.post(`${API}/risk-analysis`, {}, {
        headers: getAuthHeader()
      });
      setRiskAnalysis(response.data);
      await loadStats();
      await loadSubscriptionStatus();
      toast.success("Análise de risco concluída! (-3 créditos)");
    } catch (error) {
      console.error("Error analyzing risk:", error);
      const errorMsg = error.response?.data?.detail || "Erro ao analisar risco";
      toast.error(errorMsg);
    } finally {
      setAnalyzingRisk(false);
    }
  };

  const analyzePjPf = async () => {
    if (!businessProfile) {
      toast.error("Crie um perfil do negócio primeiro");
      return;
    }

    setAnalyzingPjpf(true);
    try {
      const response = await axios.post(`${API}/pj-pf-analysis`, {}, {
        headers: getAuthHeader()
      });
      setPjpfAnalysis(response.data);
      await loadSubscriptionStatus();
      toast.success("Análise PJ/PF concluída! (-2 créditos)");
    } catch (error) {
      console.error("Error analyzing PJ/PF:", error);
      const errorMsg = error.response?.data?.detail || "Erro ao analisar separação PJ/PF";
      toast.error(errorMsg);
    } finally {
      setAnalyzingPjpf(false);
    }
  };

  const getCrisisActionPlan = async () => {
    if (!businessProfile) {
      toast.error("Crie um perfil do negócio primeiro");
      return;
    }

    setLoadingCrisis(true);
    try {
      const response = await axios.post(`${API}/crisis-mode/action-plan`, {}, {
        headers: getAuthHeader()
      });
      setCrisisActionPlan(response.data);
      await loadSubscriptionStatus();
      toast.success("Plano de ação de crise gerado! (-3 créditos)");
    } catch (error) {
      console.error("Error getting crisis plan:", error);
      const errorMsg = error.response?.data?.detail || "Erro ao gerar plano de crise";
      toast.error(errorMsg);
    } finally {
      setLoadingCrisis(false);
    }
  };

  const analyzeExpansion = async () => {
    if (!businessProfile) {
      toast.error("Crie um perfil do negócio primeiro");
      return;
    }

    if (!expansionForm.expansion_details.trim()) {
      toast.error("Descreva o que você quer expandir");
      return;
    }

    setAnalyzingExpansion(true);
    try {
      const response = await axios.post(`${API}/expansion-analysis`, {
        expansion_type: expansionForm.expansion_type,
        expansion_details: expansionForm.expansion_details,
        investment_available: expansionForm.investment_available ? parseFloat(expansionForm.investment_available) : null,
        timeline: expansionForm.timeline || null,
        main_goal: expansionForm.main_goal || null
      }, {
        headers: getAuthHeader()
      });
      setExpansionAnalysis(response.data);
      setShowExpansionForm(false);
      await loadSubscriptionStatus();
      toast.success("Análise de expansão concluída! (-5 créditos)");
    } catch (error) {
      console.error("Error analyzing expansion:", error);
      const errorMsg = error.response?.data?.detail || "Erro ao analisar expansão";
      toast.error(errorMsg);
    } finally {
      setAnalyzingExpansion(false);
    }
  };

  const analyzeCashflow = async () => {
    if (!cashflowData.revenue || !cashflowData.expenses) {
      toast.error("Preencha receitas e despesas");
      return;
    }

    setAnalyzingCashflow(true);
    try {
      const response = await axios.post(`${API}/analyze-cashflow`, {
        revenue: parseFloat(cashflowData.revenue),
        expenses: parseFloat(cashflowData.expenses),
        period: cashflowData.period,
        description: cashflowData.description,
        business_type: businessProfile?.business_type
      }, {
        headers: getAuthHeader()
      });
      setCashflowResult(response.data);
      await loadSubscriptionStatus();
      toast.success("Análise concluída! (-2 créditos)");
    } catch (error) {
      console.error("Error analyzing cashflow:", error);
      const errorMsg = error.response?.data?.detail || "Erro ao analisar fluxo de caixa";
      toast.error(errorMsg);
    } finally {
      setAnalyzingCashflow(false);
    }
  };

  const getRiskColor = (score) => {
    if (score >= 70) return "text-red-600";
    if (score >= 50) return "text-orange-600";
    if (score >= 30) return "text-yellow-600";
    return "text-green-600";
  };

  const getRiskBadgeVariant = (level) => {
    const variants = {
      "critico": "destructive",
      "alto": "destructive",
      "medio": "secondary",
      "baixo": "default"
    };
    return variants[level] || "default";
  };

  if (authLoading) {
    return (
      <div className="loading-screen">
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#00ffff' }} />
      </div>
    );
  }

  if (!user) {
    return <AuthPage />;
  }

  return (
    <div className={`app-container ${crisisMode ? 'crisis-mode' : ''}`}>
      <header className="app-header">
        <div className="header-content">
          <div className="logo-section">
            <button 
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <TrendingUp className="logo-icon" />
            <div>
              <h1 className="app-title">AtlasIA</h1>
              {crisisMode && <Badge variant="destructive" className="crisis-badge">MODO CRISE ATIVO</Badge>}
            </div>
          </div>
          <p className="tagline">Consultoria Empresarial Inteligente e Contextual</p>
        </div>
        
        {riskAnalysis && (
          <div className="risk-indicator">
            <div className="risk-score">
              <span className="risk-label">Risco:</span>
              <span className={`risk-value ${getRiskColor(riskAnalysis.risk_score)}`}>
                {riskAnalysis.risk_score}/100
              </span>
              <Badge variant={getRiskBadgeVariant(riskAnalysis.risk_level)}>
                {riskAnalysis.risk_level.toUpperCase()}
              </Badge>
            </div>
          </div>
        )}
      </header>

      <div className="main-content">
        <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header">
            {/* Plan Badge with Credits */}
            {subscriptionStatus && (
              <div className="user-plan-badge">
                <div className={`plan-icon bg-gradient-to-br ${
                  user?.plan === 'empresarial' ? 'from-purple-500 to-purple-600' :
                  user?.plan === 'premium' ? 'from-cyan-400 to-cyan-500' :
                  user?.plan === 'basico' ? 'from-blue-500 to-blue-600' :
                  'from-gray-500 to-gray-600'
                }`}>
                  {user?.plan === 'empresarial' ? <Building2 className="w-4 h-4" /> :
                   user?.plan === 'premium' ? <Star className="w-4 h-4" /> :
                   <Crown className="w-4 h-4" />}
                </div>
                <div className="plan-info">
                  <span className="plan-name">
                    {subscriptionStatus.plan_name === 'Básico' ? 'Essencial' : subscriptionStatus.plan_name}
                  </span>
                  <span className="plan-status">
                    🎯 {subscriptionStatus.unlimited_credits 
                      ? 'Créditos Ilimitados' 
                      : `${subscriptionStatus.total_credits} créditos`}
                  </span>
                </div>
              </div>
            )}
            
            {/* Upgrade Button */}
            {(!user?.plan || user?.plan === 'free' || user?.plan === 'basico') && (
              <Button 
                className="upgrade-btn"
                onClick={() => {
                  setCurrentView('pricing');
                  setMobileMenuOpen(false);
                }}
              >
                <Star className="w-4 h-4 mr-2" />
                {user?.plan === 'basico' ? 'Fazer Upgrade' : 'Ver Planos'}
              </Button>
            )}
            
            {/* Consultancy Button - Only for Premium/Empresarial */}
            {(user?.plan === 'premium' || user?.plan === 'empresarial') && (
              <Button 
                variant="outline"
                className="consultancy-btn"
                onClick={() => {
                  setCurrentView('consultancy');
                  setMobileMenuOpen(false);
                }}
                style={{ width: '100%', marginBottom: '0.5rem' }}
              >
                <Video className="w-4 h-4 mr-2" />
                Agendar Consultoria
              </Button>
            )}
            
            {/* Admin Payments Button - Only for admins */}
            {user?.is_admin && (
              <Button
                variant="outline"
                onClick={() => {
                  setCurrentView('admin');
                  setMobileMenuOpen(false);
                }}
                style={{ width: '100%', marginBottom: '0.5rem' }}
                data-testid="admin-payments-btn"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Gestão de Pagamentos
              </Button>
            )}
            
            {/* Minha Conta */}
            <Button
              variant="outline"
              onClick={() => {
                setCurrentView('account');
                setMobileMenuOpen(false);
              }}
              style={{ width: '100%', marginBottom: '0.5rem' }}
              data-testid="my-account-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Minha Conta
            </Button>

            {/* Reembolso */}
            <Button
              variant="outline"
              onClick={() => {
                setCurrentView('refund');
                setMobileMenuOpen(false);
              }}
              style={{ width: '100%', marginBottom: '0.5rem' }}
              data-testid="refund-request-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reembolso
            </Button>

            <button 
              className={`sidebar-settings-btn ${currentView === 'settings' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('settings');
                setMobileMenuOpen(false);
              }}
              data-testid="settings-btn"
            >
              <SettingsIcon className="w-5 h-5" />
              Configurações
            </button>
            
            <Button 
              className="new-chat-btn" 
              onClick={() => {
                startNewConversation();
                setMobileMenuOpen(false);
              }}
              data-testid="new-conversation-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Conversa
            </Button>
          </div>
          
          {/* Mobile Navigation Menu */}
          <div className="mobile-nav-menu">
            <div className="mobile-nav-title">Navegação</div>
            <button 
              className={`mobile-nav-item ${activeTab === 'chat' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('chat');
                setActiveTab('chat');
                setMobileMenuOpen(false);
              }}
            >
              <MessageCircle className="w-4 h-4" />
              <span>Assistente</span>
            </button>
            <button 
              className={`mobile-nav-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('chat');
                setActiveTab('profile');
                setMobileMenuOpen(false);
              }}
            >
              <Building2 className="w-4 h-4" />
              <span>Meu Negócio</span>
            </button>
            <button 
              className={`mobile-nav-item ${activeTab === 'risk' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('chat');
                setActiveTab('risk');
                setMobileMenuOpen(false);
              }}
            >
              <Shield className="w-4 h-4" />
              <span>Análise de Risco</span>
            </button>
            <button 
              className={`mobile-nav-item ${activeTab === 'cashflow' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('chat');
                setActiveTab('cashflow');
                setMobileMenuOpen(false);
              }}
            >
              <DollarSign className="w-4 h-4" />
              <span>Fluxo de Caixa</span>
            </button>
            {crisisMode && (
              <button 
                className={`mobile-nav-item crisis ${activeTab === 'crisis' ? 'active' : ''}`}
                onClick={() => {
                  setCurrentView('chat');
                  setActiveTab('crisis');
                  setMobileMenuOpen(false);
                }}
              >
                <AlertTriangle className="w-4 h-4" />
                <span>Modo Crise</span>
              </button>
            )}
            <button 
              className={`mobile-nav-item ${activeTab === 'expansion' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('chat');
                setActiveTab('expansion');
                setMobileMenuOpen(false);
              }}
            >
              <Target className="w-4 h-4" />
              <span>Expansão</span>
            </button>
            <button 
              className={`mobile-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('chat');
                setActiveTab('dashboard');
                setMobileMenuOpen(false);
              }}
            >
              <BarChart3 className="w-4 h-4" />
              <span>Dashboard</span>
            </button>
            <button 
              className={`mobile-nav-item ${activeTab === 'documents' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('chat');
                setActiveTab('documents');
                setMobileMenuOpen(false);
              }}
            >
              <FileText className="w-4 h-4" />
              <span>Documentos</span>
            </button>
            <button 
              className={`mobile-nav-item ${currentView === 'account' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('account');
                setMobileMenuOpen(false);
              }}
            >
              <CreditCard className="w-4 h-4" />
              <span>Minha Conta</span>
            </button>
            <button 
              className={`mobile-nav-item ${currentView === 'refund' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('refund');
                setMobileMenuOpen(false);
              }}
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reembolso</span>
            </button>
          </div>
          
          <Separator className="my-4" />
          
          <ScrollArea className="conversations-list">
            <div className="conversations-container">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`conversation-item ${currentConversation?.id === conv.id && currentView === 'chat' ? 'active' : ''}`}
                  onClick={() => {
                    loadConversation(conv.id);
                    setMobileMenuOpen(false);
                  }}
                  data-testid={`conversation-${conv.id}`}
                >
                  <MessageCircle className="w-4 h-4" />
                  <div className="conversation-info">
                    <p className="conversation-title">{conv.title}</p>
                    <p className="conversation-date">
                      {new Date(conv.updated_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </aside>

        {/* Overlay for mobile menu */}
        {mobileMenuOpen && (
          <div 
            className="mobile-overlay"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        <main className="content-area">
          {currentView === 'settings' ? (
            <Settings />
          ) : currentView === 'pricing' ? (
            <PricingPage onBack={() => setCurrentView('chat')} />
          ) : currentView === 'consultancy' ? (
            <ConsultancyPage onBack={() => setCurrentView('chat')} />
          ) : currentView === 'admin' ? (
            <AdminPayments onBack={() => setCurrentView('chat')} />
          ) : currentView === 'account' ? (
            <MyAccountPage
              onBack={() => setCurrentView('chat')}
              onNavigatePricing={() => setCurrentView('pricing')}
            />
          ) : currentView === 'refund' ? (
            <RefundRequestPage onBack={() => setCurrentView('chat')} />
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="tabs-container">
              <TabsList className="tabs-list desktop-only">
                <TabsTrigger value="chat" data-testid="chat-tab">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Assistente
                </TabsTrigger>
                <TabsTrigger value="profile" data-testid="profile-tab">
                  <Building2 className="w-4 h-4 mr-2" />
                  Meu Negócio
                </TabsTrigger>
                <TabsTrigger value="risk" data-testid="risk-tab">
                  <Shield className="w-4 h-4 mr-2" />
                  Análise de Risco
                </TabsTrigger>
                <TabsTrigger value="cashflow" data-testid="cashflow-tab">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Fluxo de Caixa
                </TabsTrigger>
                {crisisMode && (
                  <TabsTrigger value="crisis" data-testid="crisis-tab" className="crisis-tab">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Modo Crise
                  </TabsTrigger>
                )}
                <TabsTrigger value="expansion" data-testid="expansion-tab">
                  <Target className="w-4 h-4 mr-2" />
                  Expansão
                </TabsTrigger>
                <TabsTrigger value="dashboard" data-testid="dashboard-tab">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Dashboard
                </TabsTrigger>
                <TabsTrigger value="documents" data-testid="documents-tab">
                  <FileText className="w-4 h-4 mr-2" />
                  Documentos
                </TabsTrigger>
              </TabsList>

              <TabsContent value="chat" className="chat-content">
                <div className="chat-container">
                  <ScrollArea className="messages-area">
                    {messages.length === 0 ? (
                      <div className="empty-chat">
                        <MessageCircle className="empty-icon" />
                        <h2>Olá! Como posso ajudar sua empresa hoje?</h2>
                        <p className="empty-subtitle">
                          Educação financeira contextual • Análise de risco • Prevenção de quebra
                        </p>
                        <div className="quick-topics">
                          <Button variant="outline" onClick={() => setInputMessage("Como posso melhorar meu fluxo de caixa?")}>
                            Fluxo de caixa
                          </Button>
                          <Button variant="outline" onClick={() => setInputMessage("Como separar finanças PJ e PF?")}>
                            Separação PJ/PF
                          </Button>
                          <Button variant="outline" onClick={() => setInputMessage("Como evitar que meu negócio quebre?")}>
                            Prevenção de crise
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="messages-list">
                        {messages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`message ${msg.role}`}
                            data-testid={`message-${msg.role}-${idx}`}
                          >
                            <div className="message-content">
                              {msg.content}
                              {msg.educational_content && (
                                <div className="educational-badge">
                                  💡 {msg.educational_content}
                                </div>
                              )}
                            </div>
                            <span className="message-time">
                              {new Date(msg.timestamp).toLocaleTimeString('pt-BR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        ))}
                        {loading && (
                          <div className="message assistant loading">
                            <div className="message-content">
                              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                              Pensando na melhor resposta...
                            </div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>

                  <div className="chat-input-container">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept=".csv,.txt,.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls"
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      title="Enviar arquivo para análise"
                      data-testid="upload-btn"
                      style={{ flexShrink: 0 }}
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                    </Button>
                    <Input
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !loading && sendMessage()}
                      placeholder="Digite sua pergunta..."
                      className="chat-input"
                      disabled={loading}
                      data-testid="chat-input"
                    />
                    <Button 
                      onClick={sendMessage} 
                      disabled={loading || !inputMessage.trim()}
                      className="send-btn"
                      data-testid="send-message-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Business Profile Tab */}
              <TabsContent value="profile" className="profile-content">
                <div className="profile-container">
                  {!businessProfile && !showProfileForm ? (
                    <Card className="welcome-card">
                      <CardHeader>
                        <CardTitle>Configure seu Perfil Empresarial</CardTitle>
                        <CardDescription>
                          Para análises personalizadas e recomendações específicas
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button onClick={() => setShowProfileForm(true)} data-testid="create-profile-btn">
                          <Building2 className="w-4 h-4 mr-2" />
                          Criar Perfil do Negócio
                        </Button>
                      </CardContent>
                    </Card>
                  ) : showProfileForm ? (
                    <Card className="profile-form-card">
                      <CardHeader>
                        <CardTitle>Dados do Seu Negócio</CardTitle>
                        <CardDescription>Quanto mais detalhes, melhor a consultoria</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="profile-form">
                          <div className="form-field">
                            <label>Tipo de Negócio</label>
                            <select
                              value={profileForm.business_type}
                              onChange={(e) => setProfileForm({...profileForm, business_type: e.target.value})}
                              className="profile-select"
                              data-testid="business-type-select"
                            >
                              <option value="bar">Bar/Restaurante</option>
                              <option value="salao">Salão de Beleza</option>
                              <option value="mei">MEI (Microempreendedor)</option>
                              <option value="prestador_servico">Prestador de Serviço</option>
                              <option value="varejo">Comércio/Varejo</option>
                              <option value="servicos">Serviços Gerais</option>
                              <option value="industria">Indústria</option>
                              <option value="tecnologia">Tecnologia/Software</option>
                              <option value="consultoria">Consultoria</option>
                              <option value="outro">Outro</option>
                            </select>
                          </div>

                          {profileForm.business_type === 'outro' && (
                            <div className="form-field">
                              <label>Especifique seu Ramo *</label>
                              <Input
                                value={profileForm.custom_business_type}
                                onChange={(e) => setProfileForm({...profileForm, custom_business_type: e.target.value})}
                                placeholder="Ex: Agência de Marketing Digital"
                                data-testid="custom-business-input"
                              />
                            </div>
                          )}

                          <div className="form-field">
                            <label>Nome do Negócio (opcional)</label>
                            <Input
                              value={profileForm.business_name}
                              onChange={(e) => setProfileForm({...profileForm, business_name: e.target.value})}
                              placeholder="Ex: Salão Bella"
                              data-testid="business-name-input"
                            />
                          </div>

                          <div className="form-field">
                            <label>Descrição do Negócio (opcional)</label>
                            <textarea
                              value={profileForm.business_description}
                              onChange={(e) => setProfileForm({...profileForm, business_description: e.target.value})}
                              placeholder="Descreva brevemente o que seu negócio faz..."
                              className="description-textarea"
                              rows="3"
                            />
                          </div>

                          <div className="form-row">
                            <div className="form-field">
                              <label>Ano de Fundação (opcional)</label>
                              <Input
                                type="number"
                                value={profileForm.foundation_year}
                                onChange={(e) => setProfileForm({...profileForm, foundation_year: e.target.value})}
                                placeholder="Ex: 2020"
                                min="1900"
                                max={new Date().getFullYear()}
                              />
                            </div>
                            <div className="form-field">
                              <label>Localização (opcional)</label>
                              <Input
                                value={profileForm.location}
                                onChange={(e) => setProfileForm({...profileForm, location: e.target.value})}
                                placeholder="Ex: São Paulo - SP"
                              />
                            </div>
                          </div>

                          <div className="form-field">
                            <label>Público-Alvo (opcional)</label>
                            <Input
                              value={profileForm.target_audience}
                              onChange={(e) => setProfileForm({...profileForm, target_audience: e.target.value})}
                              placeholder="Ex: Mulheres 25-45 anos, classe B"
                            />
                          </div>

                          <div className="form-field">
                            <label>Principais Produtos/Serviços (opcional)</label>
                            <textarea
                              value={profileForm.main_products_services}
                              onChange={(e) => setProfileForm({...profileForm, main_products_services: e.target.value})}
                              placeholder="Liste seus principais produtos ou serviços..."
                              className="description-textarea"
                              rows="2"
                            />
                          </div>

                          <div className="form-field">
                            <label>Principais Concorrentes (opcional)</label>
                            <Input
                              value={profileForm.competitors}
                              onChange={(e) => setProfileForm({...profileForm, competitors: e.target.value})}
                              placeholder="Ex: Empresa X, Empresa Y"
                            />
                          </div>

                          <Separator className="my-4" />
                          <h4 style={{marginBottom: '1rem', fontWeight: 600}}>Dados Financeiros</h4>

                          <div className="form-row">
                            <div className="form-field">
                              <label>Receita Mensal (R$) *</label>
                              <Input
                                type="number"
                                value={profileForm.monthly_revenue}
                                onChange={(e) => setProfileForm({...profileForm, monthly_revenue: e.target.value})}
                                placeholder="0.00"
                                data-testid="profile-revenue-input"
                                required
                              />
                            </div>
                            <div className="form-field">
                              <label>Despesas Mensais (R$) *</label>
                              <Input
                                type="number"
                                value={profileForm.monthly_expenses}
                                onChange={(e) => setProfileForm({...profileForm, monthly_expenses: e.target.value})}
                                placeholder="0.00"
                                data-testid="profile-expenses-input"
                                required
                              />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-field">
                              <label>Dívida Atual (R$)</label>
                              <Input
                                type="number"
                                value={profileForm.current_debt}
                                onChange={(e) => setProfileForm({...profileForm, current_debt: e.target.value})}
                                placeholder="0.00"
                                data-testid="current-debt-input"
                              />
                            </div>
                            <div className="form-field">
                              <label>Pró-labore (R$)</label>
                              <Input
                                type="number"
                                value={profileForm.prolabore}
                                onChange={(e) => setProfileForm({...profileForm, prolabore: e.target.value})}
                                placeholder="0.00"
                                data-testid="prolabore-input"
                              />
                            </div>
                          </div>

                          <div className="checkbox-field">
                            <input
                              type="checkbox"
                              id="mixes"
                              checked={profileForm.mixes_personal_business}
                              onChange={(e) => setProfileForm({...profileForm, mixes_personal_business: e.target.checked})}
                              data-testid="mixes-checkbox"
                            />
                            <label htmlFor="mixes">Misturo finanças pessoais com do negócio</label>
                          </div>

                          <div className="form-actions">
                            <Button onClick={createBusinessProfile} data-testid="save-profile-btn">
                              Salvar Perfil
                            </Button>
                            <Button variant="outline" onClick={() => setShowProfileForm(false)}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="profile-display">
                      <Card>
                        <CardHeader>
                          <CardTitle>Perfil do Negócio</CardTitle>
                          <Badge>{businessProfile.business_type}</Badge>
                        </CardHeader>
                        <CardContent>
                          <div className="profile-stats">
                            <div className="stat-item">
                              <span className="stat-label">Receita Mensal</span>
                              <span className="stat-value">R$ {businessProfile.monthly_revenue.toLocaleString('pt-BR')}</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-label">Despesas Mensais</span>
                              <span className="stat-value">R$ {businessProfile.monthly_expenses.toLocaleString('pt-BR')}</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-label">Lucro Líquido</span>
                              <span className={`stat-value ${(businessProfile.monthly_revenue - businessProfile.monthly_expenses) > 0 ? 'positive' : 'negative'}`}>
                                R$ {(businessProfile.monthly_revenue - businessProfile.monthly_expenses).toLocaleString('pt-BR')}
                              </span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-label">Dívida Atual</span>
                              <span className="stat-value">R$ {businessProfile.current_debt.toLocaleString('pt-BR')}</span>
                            </div>
                          </div>

                          <Separator className="my-6" />

                          <div className="pjpf-section">
                            <h3>Separação PJ x PF</h3>
                            {businessProfile.mixes_personal_business && (
                              <Alert variant="destructive">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertTitle>Atenção!</AlertTitle>
                                <AlertDescription>
                                  Você está misturando finanças pessoais com do negócio
                                </AlertDescription>
                              </Alert>
                            )}
                            
                            <Button 
                              onClick={analyzePjPf}
                              disabled={analyzingPjpf}
                              className="mt-4"
                              data-testid="analyze-pjpf-btn"
                            >
                              {analyzingPjpf ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analisando...</>
                              ) : (
                                <><DollarSign className="w-4 h-4 mr-2" /> Analisar Separação PJ/PF</>
                              )}
                            </Button>

                            {pjpfAnalysis && (
                              <div className="analysis-result" data-testid="pjpf-result">
                                <div className="suggested-prolabore">
                                  <p>Pró-labore Atual: <strong>R$ {pjpfAnalysis.current_prolabore.toLocaleString('pt-BR')}</strong></p>
                                  <p>Pró-labore Sugerido: <strong className="highlight">R$ {pjpfAnalysis.suggested_prolabore.toLocaleString('pt-BR')}</strong></p>
                                </div>
                                <div className="analysis-text">
                                  <h4>Análise Detalhada</h4>
                                  <p className="analysis-content">{pjpfAnalysis.analysis}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Risk Analysis Tab */}
              <TabsContent value="risk" className="risk-content">
                <div className="risk-container">
                  {!businessProfile ? (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertTitle>Configure seu perfil primeiro</AlertTitle>
                      <AlertDescription>
                        Para análise de risco, precisamos dos dados do seu negócio
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      <div className="risk-header">
                        <h2>Análise de Risco do Negócio</h2>
                        <Button 
                          onClick={analyzeRisk}
                          disabled={analyzingRisk}
                          data-testid="analyze-risk-btn"
                        >
                          {analyzingRisk ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analisando...</>
                          ) : (
                            <><Shield className="w-4 h-4 mr-2" /> Analisar Risco Agora</>
                          )}
                        </Button>
                      </div>

                      {riskAnalysis && (
                        <div className="risk-results" data-testid="risk-results">
                          <Card className="risk-score-card">
                            <CardHeader>
                              <CardTitle>Score de Risco</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="risk-score-display">
                                <div className={`score-number ${getRiskColor(riskAnalysis.risk_score)}`}>
                                  {riskAnalysis.risk_score}<span className="score-max">/100</span>
                                </div>
                                <Badge variant={getRiskBadgeVariant(riskAnalysis.risk_level)} className="risk-level-badge">
                                  {riskAnalysis.risk_level.toUpperCase()}
                                </Badge>
                              </div>
                              <Progress 
                                value={riskAnalysis.risk_score} 
                                className="risk-progress"
                              />
                              {riskAnalysis.cashflow_projection_months > 0 && riskAnalysis.cashflow_projection_months <= 6 && (
                                <Alert variant="destructive" className="mt-4">
                                  <AlertTriangle className="h-4 w-4" />
                                  <AlertTitle>URGENTE</AlertTitle>
                                  <AlertDescription>
                                    Seu caixa pode zerar em {riskAnalysis.cashflow_projection_months} meses se nada mudar
                                  </AlertDescription>
                                </Alert>
                              )}
                            </CardContent>
                          </Card>

                          {riskAnalysis.alerts.length > 0 && (
                            <Card className="alerts-card">
                              <CardHeader>
                                <CardTitle>Alertas Identificados</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="alerts-list">
                                  {riskAnalysis.alerts.map((alert, idx) => (
                                    <Alert key={idx} variant={riskAnalysis.risk_level === 'critico' ? 'destructive' : 'default'}>
                                      <AlertCircle className="h-4 w-4" />
                                      <AlertDescription>{alert}</AlertDescription>
                                    </Alert>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {riskAnalysis.recommendations.length > 0 && (
                            <Card className="recommendations-card">
                              <CardHeader>
                                <CardTitle>Recomendações Prioritárias</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <ul className="recommendations-list">
                                  {riskAnalysis.recommendations.map((rec, idx) => (
                                    <li key={idx} className="recommendation-item">
                                      <Zap className="w-4 h-4" />
                                      <span>{rec}</span>
                                    </li>
                                  ))}
                                </ul>
                              </CardContent>
                            </Card>
                          )}

                          <Card className="analysis-card">
                            <CardHeader>
                              <CardTitle>Análise Detalhada</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <p className="analysis-content">{riskAnalysis.analysis_text}</p>
                            </CardContent>
                          </Card>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </TabsContent>

              {/* Cashflow Tab */}
              <TabsContent value="cashflow" className="cashflow-content">
                <Card className="cashflow-card">
                  <CardHeader>
                    <CardTitle>Análise de Fluxo de Caixa</CardTitle>
                    <CardDescription>Análise prática e contextual do seu fluxo financeiro</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="cashflow-form">
                      <div className="form-row">
                        <div className="form-field">
                          <label>Receitas (R$)</label>
                          <Input
                            type="number"
                            value={cashflowData.revenue}
                            onChange={(e) => setCashflowData({ ...cashflowData, revenue: e.target.value })}
                            placeholder="0.00"
                            data-testid="revenue-input"
                          />
                        </div>
                        <div className="form-field">
                          <label>Despesas (R$)</label>
                          <Input
                            type="number"
                            value={cashflowData.expenses}
                            onChange={(e) => setCashflowData({ ...cashflowData, expenses: e.target.value })}
                            placeholder="0.00"
                            data-testid="expenses-input"
                          />
                        </div>
                      </div>

                      <div className="form-field">
                        <label>Período</label>
                        <select
                          value={cashflowData.period}
                          onChange={(e) => setCashflowData({ ...cashflowData, period: e.target.value })}
                          className="period-select"
                          data-testid="period-select"
                        >
                          <option value="monthly">Mensal</option>
                          <option value="quarterly">Trimestral</option>
                          <option value="yearly">Anual</option>
                        </select>
                      </div>

                      <div className="form-field">
                        <label>Contexto Adicional (opcional)</label>
                        <textarea
                          value={cashflowData.description}
                          onChange={(e) => setCashflowData({ ...cashflowData, description: e.target.value })}
                          placeholder="Ex: Tive despesa extra com equipamento novo..."
                          className="description-textarea"
                          rows="3"
                          data-testid="description-textarea"
                        />
                      </div>

                      <Button 
                        onClick={analyzeCashflow} 
                        disabled={analyzingCashflow}
                        className="analyze-btn"
                        data-testid="analyze-btn"
                      >
                        {analyzingCashflow ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analisando...</>
                        ) : (
                          <><BarChart3 className="w-4 h-4 mr-2" /> Analisar Fluxo</>
                        )}
                      </Button>
                    </div>

                    {cashflowResult && (
                      <div className="cashflow-result" data-testid="cashflow-result">
                        <Separator className="my-6" />
                        <div className="metrics-grid">
                          <div className="metric-card">
                            <p className="metric-label">Fluxo de Caixa Líquido</p>
                            <p className={`metric-value ${cashflowResult.metrics.status}`}>
                              R$ {cashflowResult.metrics.net_cashflow.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div className="metric-card">
                            <p className="metric-label">Margem</p>
                            <p className={`metric-value ${cashflowResult.metrics.status}`}>
                              {cashflowResult.metrics.margin_percentage}%
                            </p>
                          </div>
                        </div>

                        <div className="analysis-text">
                          <h3>Análise Detalhada</h3>
                          <p className="analysis-content">{cashflowResult.analysis}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Crisis Mode Tab */}
              {crisisMode && (
                <TabsContent value="crisis" className="crisis-content">
                  <div className="crisis-container">
                    <Alert variant="destructive" className="crisis-alert">
                      <AlertTriangle className="h-5 w-5" />
                      <AlertTitle className="text-lg">MODO CRISE ATIVADO</AlertTitle>
                      <AlertDescription>
                        Seu negócio está em situação crítica. Foque nas ações prioritárias abaixo.
                      </AlertDescription>
                    </Alert>

                    <Card className="crisis-card">
                      <CardHeader>
                        <CardTitle>Plano de Ação Imediato</CardTitle>
                        <CardDescription>Passos diretos para estabilizar a situação</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button 
                          onClick={getCrisisActionPlan}
                          disabled={loadingCrisis}
                          variant="destructive"
                          className="w-full mb-4"
                          data-testid="crisis-plan-btn"
                        >
                          {loadingCrisis ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando plano...</>
                          ) : (
                            <><AlertTriangle className="w-4 h-4 mr-2" /> Gerar Plano de Ação</>
                          )}
                        </Button>

                        {crisisActionPlan && (
                          <div className="crisis-plan" data-testid="crisis-plan">
                            <div className="severity-indicator">
                              <Badge variant="destructive">
                                Severidade: {crisisActionPlan.severity.toUpperCase()}
                              </Badge>
                              <p className="focus-text">Foco: {crisisActionPlan.immediate_focus}</p>
                            </div>

                            <div className="action-plan-text">
                              <p className="crisis-text">{crisisActionPlan.action_plan}</p>
                            </div>

                            <Alert className="mt-4">
                              <Info className="h-4 w-4" />
                              <AlertDescription>
                                Execute uma ação de cada vez. Não tente fazer tudo junto.
                              </AlertDescription>
                            </Alert>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              )}

              {/* Expansion Tab */}
              <TabsContent value="expansion" className="expansion-content">
                <div className="expansion-container">
                  {!businessProfile ? (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertTitle>Configure seu perfil primeiro</AlertTitle>
                      <AlertDescription>
                        Para análise de expansão, precisamos dos dados do seu negócio
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      <Card className="expansion-card">
                        <CardHeader>
                          <CardTitle>Análise de Viabilidade de Expansão</CardTitle>
                          <CardDescription>
                            Forneça detalhes sobre o que você quer expandir para uma análise personalizada
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {!showExpansionForm && !expansionAnalysis ? (
                            <Button 
                              onClick={() => setShowExpansionForm(true)}
                              data-testid="start-expansion-btn"
                            >
                              <Target className="w-4 h-4 mr-2" />
                              Começar Análise de Expansão
                            </Button>
                          ) : showExpansionForm ? (
                            <div className="expansion-form">
                              <div className="form-field">
                                <label>Tipo de Expansão *</label>
                                <select
                                  value={expansionForm.expansion_type}
                                  onChange={(e) => setExpansionForm({...expansionForm, expansion_type: e.target.value})}
                                  className="profile-select"
                                >
                                  <option value="nova_unidade">Abrir Nova Unidade/Filial</option>
                                  <option value="contratar">Contratar Mais Funcionários</option>
                                  <option value="marketing">Investir em Marketing</option>
                                  <option value="novos_produtos">Lançar Novos Produtos/Serviços</option>
                                  <option value="tecnologia">Investir em Tecnologia</option>
                                  <option value="equipamentos">Comprar Equipamentos</option>
                                  <option value="outro">Outro</option>
                                </select>
                              </div>

                              <div className="form-field">
                                <label>Descreva Sua Expansão *</label>
                                <textarea
                                  value={expansionForm.expansion_details}
                                  onChange={(e) => setExpansionForm({...expansionForm, expansion_details: e.target.value})}
                                  placeholder="Seja específico! Ex: Quero abrir uma segunda loja no bairro X, com 100m², focando em [público]. Pretendo oferecer..."
                                  className="description-textarea"
                                  rows="5"
                                  required
                                />
                                <small style={{color: '#888', fontSize: '0.85rem'}}>
                                  Quanto mais detalhes, melhor a análise!
                                </small>
                              </div>

                              <div className="form-row">
                                <div className="form-field">
                                  <label>Capital Disponível (R$)</label>
                                  <Input
                                    type="number"
                                    value={expansionForm.investment_available}
                                    onChange={(e) => setExpansionForm({...expansionForm, investment_available: e.target.value})}
                                    placeholder="0.00"
                                  />
                                </div>
                                <div className="form-field">
                                  <label>Prazo Desejado</label>
                                  <Input
                                    value={expansionForm.timeline}
                                    onChange={(e) => setExpansionForm({...expansionForm, timeline: e.target.value})}
                                    placeholder="Ex: 6 meses, 1 ano"
                                  />
                                </div>
                              </div>

                              <div className="form-field">
                                <label>Objetivo Principal</label>
                                <Input
                                  value={expansionForm.main_goal}
                                  onChange={(e) => setExpansionForm({...expansionForm, main_goal: e.target.value})}
                                  placeholder="Ex: Aumentar faturamento em 50%, atingir novo público"
                                />
                              </div>

                              <div className="form-actions">
                                <Button 
                                  onClick={analyzeExpansion}
                                  disabled={analyzingExpansion || !expansionForm.expansion_details.trim()}
                                  data-testid="analyze-expansion-btn"
                                >
                                  {analyzingExpansion ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analisando...</>
                                  ) : (
                                    <><Target className="w-4 h-4 mr-2" /> Analisar Expansão</>
                                  )}
                                </Button>
                                <Button 
                                  variant="outline" 
                                  onClick={() => setShowExpansionForm(false)}
                                >
                                  Cancelar
                                </Button>
                              </div>
                            </div>
                          ) : null}

                          {expansionAnalysis && (
                            <div className="expansion-results" data-testid="expansion-results">
                              <div className="expansion-header">
                                <Badge>{expansionAnalysis.expansion_type.replace('_', ' ').toUpperCase()}</Badge>
                                {expansionAnalysis.estimated_investment && (
                                  <p className="investment-badge">
                                    Capital: R$ {expansionAnalysis.estimated_investment.toLocaleString('pt-BR')}
                                  </p>
                                )}
                              </div>

                              <div className="readiness-indicator">
                                <div className="readiness-score">
                                  <span className="score-label">Saúde do Negócio:</span>
                                  <span className="score-value">{expansionAnalysis.current_health_score}%</span>
                                </div>
                                {expansionAnalysis.is_ready_to_expand ? (
                                  <Alert className="readiness-alert success">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertTitle>Pronto para Expandir!</AlertTitle>
                                    <AlertDescription>
                                      Seu negócio tem condições de crescer com segurança
                                    </AlertDescription>
                                  </Alert>
                                ) : (
                                  <Alert variant="default" className="readiness-alert">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertTitle>Ainda não é o momento</AlertTitle>
                                    <AlertDescription>
                                      Fortaleça sua base antes de expandir
                                    </AlertDescription>
                                  </Alert>
                                )}
                              </div>

                              <Separator className="my-6" />

                              <div className="analysis-text">
                                <h3>Análise Detalhada</h3>
                                <p className="analysis-content">{expansionAnalysis.analysis}</p>
                              </div>

                              <Button 
                                variant="outline" 
                                onClick={() => {
                                  setExpansionAnalysis(null);
                                  setShowExpansionForm(true);
                                }}
                                className="mt-4"
                              >
                                Fazer Nova Análise
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>
              </TabsContent>

              {/* Dashboard Tab */}
              <TabsContent value="dashboard" className="dashboard-content">
                <div className="dashboard-grid">
                  <Card className="stat-card" data-testid="total-conversations-card">
                    <CardHeader>
                      <CardTitle>Conversas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="stat-number">{stats.total_conversations}</p>
                    </CardContent>
                  </Card>

                  <Card className="stat-card" data-testid="total-messages-card">
                    <CardHeader>
                      <CardTitle>Mensagens</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="stat-number">{stats.total_messages}</p>
                    </CardContent>
                  </Card>

                  <Card className="stat-card risk-stat-card" data-testid="risk-stat-card">
                    <CardHeader>
                      <CardTitle>Risco Atual</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={`stat-number ${getRiskColor(stats.risk_score)}`}>
                        {stats.risk_score}/100
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="topics-card" data-testid="recent-topics-card">
                    <CardHeader>
                      <CardTitle>Tópicos Recentes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="topics-list">
                        {stats.recent_topics.map((topic, idx) => (
                          <li key={idx}>{topic}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="documents-content">
                <div style={{ padding: '1.5rem', maxWidth: '900px', margin: '0 auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <div>
                      <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.25rem' }}>Meus Documentos</h2>
                      <p style={{ color: '#888', fontSize: '0.85rem' }}>Envie arquivos financeiros para análise com IA</p>
                    </div>
                    <div>
                      <input
                        type="file"
                        id="doc-upload-input"
                        onChange={handleFileUpload}
                        accept=".csv,.txt,.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls"
                        style={{ display: 'none' }}
                      />
                      <Button
                        onClick={() => document.getElementById('doc-upload-input')?.click()}
                        disabled={uploading}
                        data-testid="documents-upload-btn"
                        style={{ background: '#00ffff', color: '#000' }}
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileUp className="w-4 h-4 mr-2" />}
                        Enviar Arquivo
                      </Button>
                    </div>
                  </div>

                  {/* Supported formats info */}
                  <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
                    <p style={{ color: '#888', fontSize: '0.8rem' }}>
                      <Info className="w-3.5 h-3.5 inline mr-1" />
                      Formatos aceitos: CSV, TXT, PDF, PNG, JPG, XLSX, XLS (max 10MB). Custo: 3 créditos por análise.
                    </p>
                  </div>

                  {/* File list */}
                  {uploadedFiles.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                      <FileText style={{ width: '48px', height: '48px', margin: '0 auto 1rem', opacity: 0.4 }} />
                      <p>Nenhum documento enviado ainda</p>
                      <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>Envie planilhas, relatórios ou extratos para análise</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {uploadedFiles.map(file => (
                        <Card key={file.id} style={{ background: '#1a1a1a', border: '1px solid #333' }}>
                          <CardContent style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                              <FileText className="w-8 h-8" style={{ color: '#00ffff', flexShrink: 0 }} />
                              <div style={{ minWidth: 0 }}>
                                <p style={{ fontWeight: '600', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} data-testid={`file-name-${file.id}`}>{file.filename}</p>
                                <p style={{ color: '#888', fontSize: '0.75rem' }}>
                                  {(file.size / 1024).toFixed(1)} KB  |  {new Date(file.created_at).toLocaleDateString('pt-BR')}
                                </p>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => analyzeFile(file.id)}
                                disabled={analyzingFile === file.id}
                                data-testid={`analyze-file-${file.id}`}
                              >
                                {analyzingFile === file.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                                Analisar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteFile(file.id)}
                                style={{ color: '#ef4444' }}
                                data-testid={`delete-file-${file.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {/* Analysis Result */}
                  {fileAnalysisResult && (
                    <Card style={{ background: '#1a1a1a', border: '1px solid #00ffff', marginTop: '1.5rem' }}>
                      <CardHeader>
                        <CardTitle style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Zap className="w-4 h-4" style={{ color: '#00ffff' }} />
                          Análise: {fileAnalysisResult.filename}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div style={{ lineHeight: '1.7', fontSize: '0.9rem' }} data-testid="file-analysis-result">
                          <ReactMarkdown>{fileAnalysisResult.analysis}</ReactMarkdown>
                        </div>
                        {fileAnalysisResult.credits_remaining !== undefined && (
                          <p style={{ color: '#888', fontSize: '0.75rem', marginTop: '1rem' }}>
                            Créditos restantes: {fileAnalysisResult.credits_remaining}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/pagamento/sucesso" element={<PaymentResultPage type="success" />} />
          <Route path="/pagamento/cancelado" element={<PaymentResultPage type="cancelled" />} />
          <Route path="/privacidade" element={<PrivacyPage />} />
          <Route path="/reembolso" element={<RefundPage />} />
          <Route path="/termos" element={<TermsPage />} />
          <Route path="/contato" element={<ContactPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

// Payment result page wrapper
function PaymentResultPage({ type }) {
  return (
    <div className="app-container" data-theme="dark">
      <PaymentResult type={type} />
    </div>
  );
}

export default App;
